/**
 * Preload priority — WHICH database the Object Explorer preloads first, and
 * HOW MANY of the rest may run at once.
 *
 * The policy is one sentence and it is the SAME for every engine: right after
 * connect, preload the database the user is actually attached to (their "home"
 * database) alone and immediately, then work through the remaining databases
 * in a bounded wave instead of flooding the server. Only the *number* in that
 * wave is engine-specific, and only because the per-database cost differs —
 * see {@link getPreloadConcurrency}.
 */

import type { DatabaseEngineId } from '@/core/engines';
import { getQueries } from './queries';
import { caseFoldFor } from '../../connections/utils/databaseNameCase';
import { isSystemDatabase } from '../../explorer/models/DatabaseTreeModels';

/** The bits of a saved/active connection this module needs. */
export interface HomeDatabaseConnection {
  database?: string;
  engine?: DatabaseEngineId | string;
  /** Oracle only: `ALTER SESSION SET CURRENT_SCHEMA` target, when configured. */
  oracleInitialSchema?: string;
}

/**
 * Conservative copy of `isValidOracleIdentifier`
 * (`electron/oracle-connection/oracleConnectString.ts`) — the rule the Oracle
 * pool factory validates `oracleInitialSchema` against before it ever reaches
 * SQL. Duplicated rather than imported because that module is main-process
 * code; the copy FAILS CLOSED, so if the two ever drift the only consequence
 * is that a schema is not prioritised — never that an unvalidated name is sent
 * to the server.
 */
const ORACLE_IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_$#]{0,127}$/;

/**
 * The database whose objects are worth loading FIRST — the one the session is
 * already attached to, so the user's first query (and its autocomplete) hits
 * warm metadata before the rest of the server has finished loading.
 *
 * Returns `undefined` when the engine has no such name to prioritise; callers
 * then just preload the server list in order.
 *
 * Note this deliberately ignores the "system databases are not preloaded" rule
 * that {@link databasesFolderExpansion} applies to the fan-out: that rule is
 * about not fetching dozens of catalogs nobody asked for. The home database is
 * the one the session sits in — when a connection carries no explicit database
 * the session sits in the engine default (`master` / `postgres` / `mysql`), and
 * that is exactly the metadata the first query needs.
 */
export function resolveHomePreloadDatabase(
  connection: HomeDatabaseConnection | undefined,
  engine?: DatabaseEngineId | string,
): string | undefined {
  // A legacy saved record with no engine dials as MSSQL (main defaults
  // `engine || 'mssql'`), so resolve it the same way here.
  const engineId = engine || connection?.engine || 'mssql';

  // Oracle divergence (cross-engine rule): the Object Explorer's "databases"
  // are SCHEMAS (`oracleQueries.databases` lists ALL_USERS), while
  // `connection.database` holds the SERVICE NAME (`oracleConnectString.ts`
  // reads `oracleServiceName || database`). So `database` is never the answer
  // here — but `oracleInitialSchema` is: the pool factory runs
  // `ALTER SESSION SET CURRENT_SCHEMA = "<value>"` on every new session, so a
  // connection that connects at all is sitting in exactly that schema.
  //
  // Taken VERBATIM, not upper-cased: the factory QUOTES the identifier, so the
  // stored value already is the catalog spelling `ALL_USERS.USERNAME` reports
  // (upper-case for the usual unquoted-schema case, because that is what the
  // user must have typed for the session callback to succeed). Upper-casing
  // here would break the rarer genuinely-lower-case schema, which works today.
  //
  // Without a configured schema there is nothing to prioritise before the
  // listing returns — guessing would fan out five listings against a schema
  // that may not exist — and Oracle pays no per-database login anyway (one
  // pool, ALTER SESSION per query), so the cost this avoids is smaller there.
  if (engineId === 'oracle') {
    const schema = connection?.oracleInitialSchema?.trim();
    return schema && ORACLE_IDENTIFIER.test(schema) ? schema : undefined;
  }

  // SQLite divergence: one file = one database. The listing is the literal
  // `SELECT 'main'` (`sqliteQueries.databases`) while `connection.database`
  // holds the file path, so the engine default is the ONLY name that can match
  // what the tree will render.
  if (engineId === 'sqlite') return getQueries(engineId).defaultDatabase.trim() || undefined;

  const configured = connection?.database?.trim();
  if (configured) return configured;
  // Same fallback `databasesService` uses when a connection carries no database.
  return getQueries(engineId).defaultDatabase.trim() || undefined;
}

/**
 * Databases in flight at once for the non-home wave, when nothing pays a login
 * per database: Oracle (one pool, schemas selected with ALTER SESSION), SQLite
 * (one file), Kusto (stateless HTTPS per request), and the mock engine.
 *
 * This bound predates the home-first policy and stays for its own reason: an
 * unbounded `Promise.all` let a many-database server fan out one doomed
 * pool-open per database in a single wave — a field session with ~64
 * unreachable databases (main pool fine, per-DB connects timing out at 30s+)
 * burned 64 concurrent opens and fired 64 `feature:db_pool_open_failed` events
 * in one burst. Bounding the wave keeps healthy servers fast (opens are
 * sub-second) while letting the driver-side per-connection breaker trip after
 * the first few failures instead of after all of them.
 */
export const DEFAULT_PRELOAD_CONCURRENCY = 8;

/**
 * Databases in flight at once for engines where a NON-home database means a
 * per-database pool — i.e. a fresh login/handshake per database
 * (`getPoolForDatabase` in the pgsql / mssql / mysql managers; the home
 * database reuses the main pool). Eight of those at once × five object types
 * each is up to 40 logins landing on the server in the same second the user
 * wants to run their first query.
 */
export const LOGIN_PER_DATABASE_PRELOAD_CONCURRENCY = 2;

/**
 * Per-engine wave size. The POLICY is identical everywhere ("don't flood the
 * server right after connect"); the number differs only because the
 * engine-internal cost of one more database in flight differs.
 *
 * | Engine  | Wave | Why                                                        |
 * |---------|------|------------------------------------------------------------|
 * | pgsql   | 2    | per-database pool ⇒ fresh login per database                |
 * | mssql   | 2    | per-database pool ⇒ fresh login (both driver + SSTS routes) |
 * | mysql   | 2    | per-database pool ⇒ fresh login per database                |
 * | oracle  | 8    | ONE pool; a "database" is a schema selected per query        |
 * | sqlite  | 8    | one file, no login at all                                    |
 * | kusto   | 8    | stateless HTTPS request per listing                          |
 * | mock    | 8    | in-process, no login                                         |
 * | (unset) | 2    | a legacy record with no engine dials as MSSQL                 |
 */
export function getPreloadConcurrency(engine?: DatabaseEngineId | string): number {
  // A legacy saved record with no engine dials as MSSQL (main defaults
  // `engine || 'mssql'`), so it pays MSSQL's per-database login too.
  if (!engine) return LOGIN_PER_DATABASE_PRELOAD_CONCURRENCY;
  switch (engine) {
    case 'pgsql':
    case 'mssql':
    case 'mysql':
      return LOGIN_PER_DATABASE_PRELOAD_CONCURRENCY;
    default:
      return DEFAULT_PRELOAD_CONCURRENCY;
  }
}

/** What the connection-wide preload should fetch, and in which order. */
export interface PreloadOrder {
  /** Preloaded alone, before the wave. Absent when there is nothing to prioritise. */
  homeDatabase?: string;
  /** Every database still to fetch, in server order (home included when it leads). */
  databases: string[];
}

/**
 * Splits the server's database list into "home first, then the rest".
 *
 * The leader is always the LISTED spelling, because everything downstream —
 * `preloadedData`, the react-query cache, every tree node id — is keyed by it,
 * and this runs after the listing so we have it.
 *
 * Exact match wins. On engines that fold identifiers (mssql, mysql) a
 * configured `AdventureWorks` also matches a listed `adventureworks`, and the
 * LISTED entry becomes the leader — the wave then works entirely in the
 * server's spelling, which is why this is safe here and was not when the
 * preload started before the listing. pgsql / oracle / sqlite stay exact:
 * there `AdventureWorks` and `adventureworks` really are two databases.
 */
export function planPreloadOrder(
  databases: string[],
  homeDatabase: string | undefined,
  engine?: DatabaseEngineId | string,
): PreloadOrder {
  const leader = findListedHome(databases, homeDatabase, engine);
  // System databases are not preloaded — nobody asked for a catalog listing.
  // The LEADER is the exception, and the reason this takes the unfiltered list:
  // a connection with no configured database sits in the engine default, which
  // is itself a system database, and that is precisely the metadata the user's
  // first query needs.
  const rest = databases.filter((db) => db !== leader && !isSystemDatabase(db));
  return leader ? { homeDatabase: leader, databases: [leader, ...rest] } : { databases: rest };
}

/** The listing's own spelling of the home database, or `undefined`. */
function findListedHome(
  databases: string[],
  homeDatabase: string | undefined,
  engine?: DatabaseEngineId | string,
): string | undefined {
  if (!homeDatabase) return undefined;
  if (databases.includes(homeDatabase)) return homeDatabase;
  const fold = caseFoldFor(engine);
  const folded = fold(homeDatabase);
  return databases.find((database) => fold(database) === folded);
}
